import {
  CloseOutlined,
  CopyAllOutlined,
  Email,
  ManageSearch,
} from '@mui/icons-material'
import ArrowCircleDownSharpIcon from '@mui/icons-material/ArrowCircleDownSharp'
import ArrowCircleUpSharpIcon from '@mui/icons-material/ArrowCircleUpSharp'
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  TablePagination,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import {
  PaginationState,
  SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { bookingsAPI } from 'api/bookingsAPI'
import {
  BookingSearchRequestData,
  BookingSearchResult,
} from 'api/models/BookingSearch'
import { bookingStatus } from 'api/models/Common/JyrneyEnums'
import AddressDisplay from 'components/Common/AddressDisplay/AddressDisplay'
import DisplayDate from 'components/Common/DisplayDates/DisplayDate'
import { hoursDisplay } from 'components/Common/DisplayValues/hoursMinuteDisplay'
import React, { useEffect } from 'react'

import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { copyBooking } from 'reducers/bookingsReducer'
import handleCancelBooking from '../Common/CancelBooking'

type BookingTableProps = {
  searchOptions: BookingSearchRequestData
  setBookingId: any
  openModal: boolean
  returnOpenModal: any
}

const BookingTable = ({
  searchOptions,
  setBookingId,
  openModal,
  returnOpenModal,
}: BookingTableProps) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const theme = useTheme<Theme>()
  const isMdDown = useMediaQuery(theme.breakpoints.down('lg'))

  const styles = {
    bookingsTableRoot: {
      width: '100%',
      fontSize: 'var(--fs-13, 0.8125rem)',
      borderCollapse: 'collapse',
      borderSpacing: 0,
      border: 'none',
      borderTop: `1px solid ${theme.palette.divider ?? 'var(--jy-ink-200, #e2e6ea)'}`,
    },
    bookingTableHeader: {
      color: '#FFF',
      fontSize: 'var(--fs-14, 0.875rem)',
      border: 'none',
      backgroundColor:
        theme.components?.BookingTable?.headerColour || '#000000',
    },
    bookingTableCell: {
      padding: isMdDown ? '4px 4px' : '8px 4px',
      border: 'none',
      borderTop: `1px solid ${theme.palette.divider ?? 'var(--jy-ink-200, #e2e6ea)'}`,
    },
    bookingTableRow: {
      fontSize: 'var(--fs-13, 0.8125rem)',
    },
  }
  const [columnVisibility, setColumnVisibility] = React.useState({
    Company: false,
  })
  const [selectedRow, setSelectedRow] = React.useState('-10')
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [bookingSearchOptions, setBookingSearchOptions] =
    React.useState(searchOptions)

  const cancelBooking = async (bookingId: number) => {
    try {
      bookingsAPI.cancelBooking(bookingId)
    } catch (error) {
      console.log('cancel error', error)
    }
  }

  const columnHelper = createColumnHelper<BookingSearchResult>()
  const columns = React.useMemo(
    () => [
      columnHelper.accessor('bookingReference', {
        id: 'bookingReference',
        header: () => <span>Booking Reference</span>,
        cell: (info) => <span>{info.getValue()}</span>,
      }),
      columnHelper.accessor((row) => row.client.companyName, {
        header: 'Company',
        enableSorting: false,
        enableHiding: true,
      }),
      columnHelper.accessor('when', {
        header: () => <span>When</span>,
        cell: (info) => (
          <span>
            {DisplayDate({
              date: info.row.original.when,
              timeZone: info.row.original.timeZone || 'Europe/London',
              viewType: 'actual',
            })}
          </span>
        ),
        // cell: (info) => console.log('when row: ', info),
      }),
      columnHelper.accessor((row) => row, {
        header: 'Pickup',
        cell: (info) => {
          return (
            <span>
              {AddressDisplay({
                address: info.row.original.pickup,
                line1Only: isMdDown,
              })}
            </span>
          )
        },
        enableSorting: false,
      }),
      columnHelper.accessor(
        (row) => (row.dropOff ? row.dropOff : 'Hourly Trip'),
        {
          header: 'Drop Off',
          cell: (info) => {
            if (info.row.original.dropOff.addressLine1) {
              return (
                <span>
                  {AddressDisplay({
                    address: info.getValue(),
                    line1Only: isMdDown,
                  })}
                </span>
              )
            } else {
              return (
                <span>
                  Hourly Trip{' '}
                  {hoursDisplay({
                    minutes: info.row.original.directedJourneyTime * 60,
                  })}
                </span>
              )
            }
          },
          enableSorting: false,
        },
      ),
      columnHelper.accessor(
        (row) =>
          row.passengers.find((obj) => {
            return obj.isLeadPassenger
          })?.name,
        {
          header: 'Lead Passenger',
          enableSorting: false,
          //TODO: Iterate through the array to get the lead passenger
        },
      ),
      columnHelper.accessor('bookingStatusTypeName', {
        header: 'Status',
        enableSorting: false,
        cell: (info) => <span>{bookingStatus[info.getValue()]}</span>,
      }),
      columnHelper.accessor('parentBookingId', {
        id: 'actions',
        header: 'Manage',
        enableSorting: false,
        cell: (info) => (
          <Box sx={{ display: 'flex', flexWrap: 'nowrap' }}>
            <Tooltip
              title={`View ${
                bookingStatus[info.row.original.bookingStatusTypeName] ===
                'Confirmed'
                  ? '/ Edit'
                  : ''
              } Booking`}
            >
              <IconButton
                onClick={() => {
                  setBookingId(info.row.original.id)
                  returnOpenModal(true)
                }}
              >
                <ManageSearch />
              </IconButton>
            </Tooltip>

            <Tooltip title="Resend booking confirmation">
              <IconButton
                onClick={() =>
                  bookingsAPI.resendCommunication(
                    info.row.original.id,
                    'Booking_Confirmation',
                  )
                }
              >
                <Email />
              </IconButton>
            </Tooltip>
            <Tooltip title="Copy booking">
              <IconButton
                onClick={() => {
                  dispatch(copyBooking(info.row.original.id))
                  history.push('/admin/bookings/create/account?type=taxi')
                }}
              >
                <CopyAllOutlined />
              </IconButton>
            </Tooltip>
            {info.row.original.bookingStatusTypeName === 'Confirmed' ? (
              <Tooltip title="Cancel booking">
                <IconButton
                  sx={{
                    '&:hover': {
                      backgroundColor: 'red',
                      color: 'white !important',
                    },
                  }}
                  onClick={async () => {
                    const booking = await bookingsAPI.getBookingWithPolicy(
                      info.row.original.id,
                    )
                    const bookingJobs = await bookingsAPI.getBookingJobs(
                      info.row.original.id,
                    )

                    handleCancelBooking({
                      action: () => cancelBooking(info.row.original.id),
                      bookingReference: info.row.original.bookingReference,
                      bookingJobs: bookingJobs.value,
                      booking: booking,
                    })
                  }}
                >
                  <CloseOutlined />
                </IconButton>
              </Tooltip>
            ) : (
              <></>
            )}
          </Box>
        ),
      }),
    ],
    [isMdDown],
  )

  const [{ pageIndex, pageSize }, setPagination] =
    React.useState<PaginationState>({
      pageIndex: 0,
      pageSize: 10,
    })

  const pagination = React.useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize],
  )

  useEffect(() => {
    const newSearch = { ...bookingSearchOptions, ...searchOptions }
    if (searchOptions.clientName) {
      setColumnVisibility({ Company: false })
    } else {
      setColumnVisibility({ Company: true })
    }
    setBookingSearchOptions(newSearch)
  }, [searchOptions])

  const currentOrderBy = bookingSearchOptions.orderBy

  useEffect(() => {
    if (!sorting.length) return

    const [primarySort] = sorting
    const nextOrderByTypeName =
      (primarySort?.id as string | undefined) ?? 'When'
    const nextIsDescending = primarySort?.desc ?? false

    if (
      nextIsDescending !== currentOrderBy?.isDescending ||
      nextOrderByTypeName !== currentOrderBy?.bookingOrderByTypeName
    ) {
      setBookingSearchOptions((prev) => ({
        ...prev,
        orderBy: {
          isDescending: nextIsDescending,
          bookingOrderByTypeName: nextOrderByTypeName,
        },
      }))
    }
  }, [sorting, currentOrderBy])

  const { data, isLoading, refetch }: any = useQuery({
    queryKey: [
      'getMyActiveBookings',
      bookingSearchOptions,
      pagination,
      openModal,
    ],
    queryFn: async () => {
      return bookingsAPI.bookingsSearch(
        { data: bookingSearchOptions },
        { pageIndex: pageIndex, pageSize: pageSize },
      )
    },
    refetchOnWindowFocus: false,
    staleTime: 2000,
  })

  useEffect(() => {
    refetch()
  }, [openModal])

  const table = useReactTable({
    data: data?.value,
    columns,
    pageCount: Math.ceil(data?.size / pageSize) ?? -1,
    state: {
      sorting,
      pagination,
      columnVisibility,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    manualPagination: true,
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
  })

  if (isLoading)
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          justifyContent: 'center',
          gap: '20px',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography
            variant="h4"
            textAlign={'center'}
            sx={{ paddingTop: '30px' }}
          >
            Loading Data
          </Typography>
          <Typography variant="body1" textAlign={'center'}>
            Data will be shown shortly, if available
          </Typography>
        </Box>
        <CircularProgress />
      </Box>
    )

  if (data?.value.length < 1)
    return (
      <>
        <Typography
          variant="h4"
          textAlign={'center'}
          sx={{ paddingTop: '30px' }}
        >
          No Results Found
        </Typography>
        <Typography variant="body1" textAlign={'center'}>
          Please try to amend the above search criteria
        </Typography>
      </>
    )

  return (
    <Grid container>
      <Grid item xs={12}>
        <Grid item xs={12}>
          <div className="p-2">
            <table style={styles.bookingsTableRoot}>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} style={styles.bookingTableHeader}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id} style={styles.bookingTableCell}>
                        {header.isPlaceholder ? null : (
                          // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                          <div
                            {...{
                              className: header.column.getCanSort()
                                ? 'cursor-pointer select-none'
                                : '',
                              onClick: header.column.getToggleSortingHandler(),
                            }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                            {{
                              asc: (
                                <ArrowCircleUpSharpIcon
                                  sx={{
                                    marginBottom: '-5px',
                                  }}
                                />
                              ),
                              desc: (
                                <ArrowCircleDownSharpIcon
                                  sx={{
                                    marginBottom: '-5px',
                                  }}
                                />
                              ),
                            }[header.column.getIsSorted() as string] ?? null}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr
                    key={`${row.original.bookingReference}_${row.index}`}
                    style={
                      (styles.bookingTableRow,
                      {
                        backgroundColor:
                          row.id === selectedRow ? '#cccccc' : '#ffffff',
                        cursor: 'pointer',
                      })
                    }
                    onClick={(e) => {
                      if (
                        e.target instanceof SVGElement ||
                        (e.target as HTMLElement).closest('svg')
                      ) {
                        return
                      }

                      if (
                        e.target instanceof HTMLButtonElement ||
                        (e.target as HTMLElement).closest('button')
                      ) {
                        return
                      }
                      setBookingId(row.original.id)
                      setSelectedRow(row.original.id)
                      returnOpenModal(true)
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} style={styles.bookingTableCell}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="h-2" />
          </div>
        </Grid>
        <Grid item xs={12}>
          <TablePagination
            component="div"
            count={data?.size ?? 0}
            page={pageIndex}
            onPageChange={(
              event: React.MouseEvent<HTMLButtonElement> | null,
              newPage: number,
            ) => {
              table.setPageIndex(newPage)
            }}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) =>
              table.setPageSize(Number(e.target.value))
            }
          />
        </Grid>
      </Grid>
    </Grid>
  )
}
export default BookingTable
